import { useState, useRef, useEffect } from 'react';
import { IconChevronDown, IconCheck, IconX } from '@tabler/icons-react';
import {
    Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../ui/command';
import { cn } from '@/lib/utils';

/**
 * Searchable multi-select combobox with removable chips (shadcn Command + cmdk).
 * Renders the dropdown inline (no portal) so it works reliably inside a
 * Radix Sheet/Dialog focus trap.
 *
 * value:   array of selected values
 * options: [{ value, label, keywords? }]  (keywords: extra words the search also matches)
 * max:     optional cap on how many may be picked (further rows are greyed out)
 */
export default function MultiSelect({
    value = [],
    onChange,
    options = [],
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found',
    disabled = false,
    max,
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selectedValues = Array.isArray(value) ? value : [];
    const full = max != null && selectedValues.length >= max;
    const toggle = (val) => {
        if (full && !selectedValues.includes(val)) return;
        const next = selectedValues.includes(val)
            ? selectedValues.filter(v => v !== val)
            : [...selectedValues, val];
        onChange(next);
    };

    const selectedOptions = options.filter(o => selectedValues.includes(o.value));

    return (
        <div ref={ref} className="relative">
            <div
                onClick={() => !disabled && setOpen(o => !o)}
                className={cn(
                    'w-full min-h-[38px] flex items-center gap-1.5 flex-wrap px-2 py-1.5 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm cursor-pointer focus-within:ring-2 focus-within:ring-orange-500',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                {selectedOptions.length === 0 && (
                    <span className="text-zinc-400 px-1">{placeholder}</span>
                )}
                {selectedOptions.map(opt => (
                    <span
                        key={opt.value}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs"
                    >
                        {opt.label}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggle(opt.value); }}
                            className="text-orange-500 hover:text-orange-700"
                        >
                            <IconX size={11} />
                        </button>
                    </span>
                ))}
                <IconChevronDown size={16} className="shrink-0 text-zinc-400 ml-auto" />
            </div>

            {open && (
                <div className="absolute z-[610] left-0 right-0 mt-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg">
                    <Command>
                        <CommandInput placeholder={searchPlaceholder} autoFocus />
                        <CommandList>
                            <CommandEmpty>{emptyText}</CommandEmpty>
                            <CommandGroup>
                                {options.map(opt => {
                                    const checked = selectedValues.includes(opt.value);
                                    return (
                                        <CommandItem
                                            key={opt.value}
                                            value={opt.label}
                                            keywords={opt.keywords}
                                            onSelect={() => toggle(opt.value)}
                                            className={cn(full && !checked && 'opacity-40')}
                                        >
                                            <span
                                                className={cn(
                                                    'flex h-4 w-4 items-center justify-center rounded border shrink-0',
                                                    checked
                                                        ? 'bg-orange-500 border-orange-500 text-white'
                                                        : 'border-zinc-300 dark:border-zinc-600'
                                                )}
                                            >
                                                {checked && <IconCheck size={12} />}
                                            </span>
                                            <span className="truncate">{opt.label}</span>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </div>
            )}
        </div>
    );
}
